import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import { FormFeedback } from "reactstrap";
import { useImmer } from "use-immer";
import InputControl from "shared/components/InputControl";
import { strongPassword } from "utils";
import { AuthLayout, ModalLayout, PasswordToggle } from "shared";
import { useResetPassword } from "..";
import loginPasswrodGraphics from "assets/images/password_generate.jpg";
import checkGraphics from "assets/images/check-graphics.svg";

export const ResetPassword = () => {
  const { payload, setPayload, mutationFn, modalState, onCloseModal } =
    useResetPassword();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailAddress = searchParams.get("email");
  const passwordResetToken = searchParams.get("token");

  const { newPassword, confirmPassword } = payload;
  const [passwordShown, setPasswordShown] = useImmer({
    newPassword: false,
    confirmPassword: false,
  });
  const [update, forceUpdate] = useState();
  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      validators: strongPassword,
    })
  );

  const onHandleChange = (e) => {
    const { name, value } = e.target;
    setPayload((draft) => {
      draft[name] = value;
    });
  };

  const togglePassword = (type) => {
    setPasswordShown((draft) => {
      draft[type] = !draft[type];
      return draft;
    });
  };

  const onSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (validator.current.allValid()) {
      mutationFn.mutate({
        ...payload,
        emailAddress,
        passwordResetToken,
      });
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  const newOnCloseModal = () => {
    onCloseModal();
    navigate("/login", { replace: true });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    validator.current.showMessageFor(name);
    if (name === "newPassword") {
      forceUpdate(2);
    } else {
      forceUpdate(3);
    }
  };

  return (
    <AuthLayout leftDivImage={loginPasswrodGraphics}>
      <form>
        <h2>Reset password</h2>
        <p className="form-auth-sm-txt">Create new password for your account</p>
        <div className="mb-3 position-relative form-group has-icon-right change_password">
          <label className="form-label">New password</label>
          <InputControl
            name="newPassword"
            type={passwordShown.newPassword ? "text" : "password"}
            className="form-control"
            disabled={mutationFn.isPending}
            onChange={onHandleChange}
            invalid={validator.current.message(
              "newPassword",
              newPassword,
              "required|min:8|upper|lower|digit|special"
            )}
            onBlur={handleBlur}
          />
          <PasswordToggle
            passwordShown={passwordShown?.newPassword}
            togglePassword={() => togglePassword("newPassword")}
          />
          <FormFeedback>
            {validator.current.message(
              "newPassword",
              newPassword,
              "required|min:8|upper|lower|digit|special"
            )}
          </FormFeedback>
        </div>
        <div className="mb-3 position-relative form-group has-icon-right change_password">
          <label className="form-label">Confirm password</label>
          <InputControl
            name="confirmPassword"
            type={passwordShown.confirmPassword ? "text" : "password"}
            className="form-control"
            disabled={mutationFn.isPending}
            onChange={onHandleChange}
            invalid={validator.current.message(
              "confirmPassword",
              confirmPassword,
              `required|in:${newPassword}`
            )}
          />
          <PasswordToggle
            passwordShown={passwordShown?.confirmPassword}
            togglePassword={() => togglePassword("confirmPassword")}
            onBlur={handleBlur}
          />
          <FormFeedback>
            {validator.current.message(
              "confirmPassword",
              confirmPassword,
              `required|in:${newPassword}`,
              {
                messages: {
                  in: "Password and confirm password must be same!",
                },
              }
            )}
          </FormFeedback>
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          onClick={(e) => onSubmit(e)}
          disabled={mutationFn.isPending}
        >
          Submit
        </button>
      </form>

      {modalState.actionFrom === "passwordReset" && (
        <ModalLayout
          isOpen={modalState.isOpen}
          title={"You’re good to go!"}
          message={"Your password was reset successfully."}
          onConfirm={newOnCloseModal}
          onCancel={newOnCloseModal}
          confirmButtonLabel={"Go To Login"}
          icon={checkGraphics}
        />
      )}
    </AuthLayout>
  );
};
